import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import createApiClient from "../../utils/createApiClient";
import { loginSuccess } from "./authSlice";

export interface RegisterUserData {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
}

export interface RegisteredUser {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  isActive: boolean;
  role: {
    id: string;
    rolename: string;
  };
  profileImg: string;
  mobileNo: string;
}

export interface RegisterApiResponse {
  success: boolean;
  message: string;
  data?: {
    user: RegisteredUser;
    accessToken: string;
  };
}

export interface LoginUserData {
  email: string;
  password: string;
}

export interface LoginResponseUser {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  isActive: boolean;
  role: {
    id: string;
    rolename: string;
  };
  profileImg: string;
  mobileNo: string;
}

export interface LoginApiResponse {
  success: boolean;
  message: string;
  data?: {
    user: LoginResponseUser;
    accessToken: string;
  };
}

export interface ProfileResponseUser {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  isActive: boolean;
  role: {
    id: string;
    rolename: string;
  };
  profileImg: string;
  mobileNo: string;
  subscription: {
    id: string;
    planName: string;
    isEmbedded: boolean;
    isWhiteList: boolean;
    isFleetEstimator: boolean;
    isSiteEstimator: boolean;
    price: number;
    quotaLimit: number;
    quotaUsed: number;
    startDate: string;
    endDate?: string;
    status: string;
    stripeCustomerId: string;
    stripeSubscriptionId: string;
    addOns: { type: string; quantity: number }[];
  } | null;
}

export interface ProfileApiResponse {
  success: boolean;
  message: string;
  data?: {
    user: ProfileResponseUser;
  };
}

export interface SendOTPData {
  email: string;
}

export interface SendOTPApiResponse {
  success: boolean;
  message: string;
}

export interface VerifyOTPData {
  email: string;
  otp: string;
}

export interface VerifyOTPApiResponse {
  success: boolean;
  message: string;
}

export interface ResetPasswordData {
  email: string;
  newPassword: string;
}

export interface ResetPasswordApiResponse {
  success: boolean;
  message: string;
}

function isAxiosError(error: unknown): error is import("axios").AxiosError {
  return (error as import("axios").AxiosError).isAxiosError !== undefined;
}

// REGISTER USER ACTION
export const registerNewUser = createAsyncThunk<
  RegisterApiResponse,
  RegisterUserData,
  { rejectValue: string }
>("auth/registerNewUser", async (formData, { rejectWithValue }) => {
  try {
    const response = await createApiClient.post<RegisterApiResponse>(
      "/auth/register",
      {
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        password: formData.password,
      }
    );

    if (response.data.success) {
      toast.success(response.data.message || "User registered successfully");

      // Store token and user info in localStorage
      const { user, accessToken } = response.data.data || {};
      if (user && accessToken) {
        localStorage.setItem(
          "user",
          JSON.stringify({
            id: user.id,
            firstname: user.firstname,
            lastname: user.lastname,
            email: user.email,
            isActive: user.isActive,
            role: user.role.rolename,
            profileImg: user.profileImg,
            mobileNo: user.mobileNo,
            subscription: null,
          })
        );
        localStorage.setItem("token", accessToken);
      }

      return response.data;
    } else {
      toast.error(response.data.message || "Registration failed");
      return rejectWithValue(response.data.message || "Registration failed");
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";

    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as RegisterApiResponse).message || "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// LOGIN USER ACTION
export const loginUser = createAsyncThunk<
  LoginApiResponse,
  LoginUserData,
  { rejectValue: string }
>("auth/loginUser", async (formData, { rejectWithValue, dispatch }) => {
  try {
    const response = await createApiClient.post<LoginApiResponse>(
      "/auth/login",
      {
        email: formData.email,
        password: formData.password,
      }
    );

    if (response.data.success) {
      toast.success(response.data.message || "Login successful");

      const { user, accessToken } = response.data.data || {};
      if (user && accessToken) {
        localStorage.setItem(
          "user",
          JSON.stringify({
            id: user.id,
            firstname: user.firstname,
            lastname: user.lastname,
            email: user.email,
            isActive: user.isActive,
            role: user.role.rolename,
            profileImg: user.profileImg || "",
            mobileNo: user.mobileNo || "",
            subscription: null,
          })
        );
        localStorage.setItem("token", accessToken);
        dispatch(
          loginSuccess({
            user: {
              email: formData.email,
              role: user.role.rolename.toLowerCase(),
            },
          })
        );
      }
      return response.data;
    } else {
      toast.error(response.data.message || "Login failed");
      return rejectWithValue(response.data.message);
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as LoginApiResponse).message || "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET AUTHENTICATED USER ACTION
export const getAuthenticatedUser = createAsyncThunk<
  ProfileApiResponse,
  void,
  { rejectValue: string }
>("auth/getAuthenticatedUser", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<ProfileApiResponse>(
      "/auth/profile"
    );

    if (response.data.success) {
      const { user } = response.data.data || {};
      if (user) {
        localStorage.setItem(
          "user",
          JSON.stringify({
            id: user.id,
            firstname: user.firstname,
            lastname: user.lastname,
            email: user.email,
            isActive: user.isActive,
            role: user.role.rolename,
            subscription: user.subscription,
            profileImg: user.profileImg || "",
            mobileNo: user.mobileNo || "",
          })
        );
      }
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to retrieve user details");
      return rejectWithValue(
        response.data.message || "Failed to retrieve user details"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as ProfileApiResponse).message || "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// SEND OTP ACTION
export const sendOTP = createAsyncThunk<
  SendOTPApiResponse,
  SendOTPData,
  { rejectValue: string }
>("auth/sendOTP", async (formData, { rejectWithValue }) => {
  try {
    const response = await createApiClient.post<SendOTPApiResponse>(
      "/auth/send-otp",
      {
        email: formData.email,
      }
    );

    if (response.data.success) {
      toast.success(response.data.message || "OTP sent successfully");
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to send OTP");
      return rejectWithValue(response.data.message || "Failed to send OTP");
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as SendOTPApiResponse).message || "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// VERIFY OTP ACTION
export const verifyOTP = createAsyncThunk<
  VerifyOTPApiResponse,
  VerifyOTPData,
  { rejectValue: string }
>("auth/verifyOTP", async (formData, { rejectWithValue }) => {
  try {
    const response = await createApiClient.post<VerifyOTPApiResponse>(
      "/auth/verify-otp",
      {
        email: formData.email,
        otp: formData.otp,
      }
    );

    if (response.data.success) {
      toast.success(response.data.message || "OTP verified successfully");
      return response.data;
    } else {
      toast.error(response.data.message || "OTP verification failed");
      return rejectWithValue(
        response.data.message || "OTP verification failed"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as VerifyOTPApiResponse).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

export const resetPassword = createAsyncThunk<
  ResetPasswordApiResponse,
  ResetPasswordData,
  { rejectValue: string }
>("auth/resetPassword", async (formData, { rejectWithValue }) => {
  try {
    const response = await createApiClient.post<ResetPasswordApiResponse>(
      "/auth/reset-password",
      {
        email: formData.email,
        newPassword: formData.newPassword,
      }
    );

    if (response.data.success) {
      toast.success(response.data.message || "Password reset successfully");
      return response.data;
    } else {
      toast.error(response.data.message || "Password reset failed");
      return rejectWithValue(response.data.message || "Password reset failed");
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as ResetPasswordApiResponse).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});
